import type { LanguageRegionCode } from '@alltrails/core/types/LanguageRegionCode';
import type { IntlShape } from 'react-intl';
import { FAQTrail } from '../../../types/locationDetailsPages/shared';
import { TrailFaqLink } from '../FaqLink';

export type SeasonNameType = 'spring' | 'summer' | 'fall' | 'winter';

export const getSeasonQuestion = (locationName: string, seasonName: SeasonNameType, intl: IntlShape) => {
  return {
    spring: intl.formatMessage({ defaultMessage: 'What are the best spring hikes in {locationName}?' }, { locationName }),
    summer: intl.formatMessage({ defaultMessage: 'What are the best summer hikes in {locationName}?' }, { locationName }),
    fall: intl.formatMessage({ defaultMessage: 'What are the best fall hikes in {locationName}?' }, { locationName }),
    winter: intl.formatMessage({ defaultMessage: 'What are the best winter hikes in {locationName}?' }, { locationName })
  }[seasonName];
};

type SeasonAnswerProps = {
  locationName: string;
  seasonName: SeasonNameType;
  trails: FAQTrail[];
  languageRegionCode: LanguageRegionCode;
  intl: IntlShape;
};

const trailLinksList = (trails: FAQTrail[], languageRegionCode: LanguageRegionCode, intl: IntlShape) => {
  const trailFaqLinks = trails.map(trail => <TrailFaqLink key={trail.slug} languageRegionCode={languageRegionCode} trail={trail} />);

  if (trailFaqLinks.length === 0) return null;
  if (trailFaqLinks.length === 1) return trailFaqLinks[0];
  return intl.formatList(trailFaqLinks, { type: 'conjunction' });
};

export const SeasonAnswer = ({ locationName, seasonName, trails, languageRegionCode, intl }: SeasonAnswerProps) => {
  const trailLinks = trailLinksList(trails, languageRegionCode, intl);

  if (!trailLinks) return null;

  return {
    spring: intl.formatMessage(
      {
        defaultMessage:
          '{trailsCount, plural, one {A popular spring hiking trail in {locationName} is {trailLinks}} other {Some popular spring hiking trails in {locationName} are {trailLinks}}}.'
      },
      { locationName, trailLinks, trailsCount: trails.length }
    ),
    summer: intl.formatMessage(
      {
        defaultMessage:
          '{trailsCount, plural, one {A popular summer hiking trail in {locationName} is {trailLinks}} other {Some popular summer hiking trails in {locationName} are {trailLinks}}}.'
      },
      { locationName, trailLinks, trailsCount: trails.length }
    ),
    fall: intl.formatMessage(
      {
        defaultMessage:
          '{trailsCount, plural, one {A popular fall hiking trail in {locationName} is {trailLinks}} other {Some popular fall hiking trails in {locationName} are {trailLinks}}}.'
      },
      { locationName, trailLinks, trailsCount: trails.length }
    ),
    winter: intl.formatMessage(
      {
        defaultMessage:
          '{trailsCount, plural, one {A popular winter hiking trail in {locationName} is {trailLinks}} other {Some popular winter hiking trails in {locationName} are {trailLinks}}}.'
      },
      { locationName, trailLinks, trailsCount: trails.length }
    )
  }[seasonName];
};
